import { Box } from "@mui/material";
import { FlowerItem } from "src/components/charts/flower/FlowerItem";
import { senecPrimaryColors, senecSecondaryColors } from "src/styles/ekp-theme/senecEkpTheme";
import { Fragment } from "react";
import { Stem } from "src/components/charts/flower/Stem";

export type Petal = {
    title: string;
    value: string;
    color: string;
    direction?: "IN" | "OUT" | "NONE";
    errorMessage?: string;
    inactive?: boolean;
};

export interface FlowerProps {
    items: Petal[];
    legend: string;
    showEnergyFlows?: boolean;
    radius?: number;
    "data-testid"?: string;
}

const getFlowDirection = (showEnergyFlows: boolean | undefined, petal: Petal) => {
    if (!showEnergyFlows || petal.inactive || petal.direction === "NONE") {
        return undefined;
    }
    return petal.direction === "IN" ? "fromPetal" : "toPetal";
};

export const Flower = (props: FlowerProps) => {
    const numberOfFlowerItems = props.items.length;
    const RADIUS = props.radius || 120;

    return (
        <Box data-testid={props["data-testid"]} sx={{ width: "400px", height: "400px", position: "relative" }}>
            <Box sx={{ ...centerPointStyles }} />
            {props.items.map((petal, index) => (
                <Fragment key={index}>
                    <Box
                        sx={{
                            position: "absolute",
                            width: "min-content",
                            top: "50%",
                            left: "50%",
                            transform: formatPetalPosition(numberOfFlowerItems, RADIUS, index),
                            zIndex: 40,
                        }}
                    >
                        <FlowerItem
                            title={petal.title}
                            value={petal.value}
                            color={petal.color}
                            errorMessage={petal.errorMessage}
                            inactive={petal.inactive}
                            legend={props.legend}
                        />
                    </Box>
                    {petal.direction != "NONE" && !petal.inactive && !petal.errorMessage && (
                        <Stem
                            index={index}
                            color={petal.color}
                            numberOfPetals={numberOfFlowerItems}
                            flowDirection={getFlowDirection(props.showEnergyFlows, petal)}
                        />
                    )}
                </Fragment>
            ))}
        </Box>
    );
};

export function calculatePetalPosition(
    numberOfPetals: number,
    radius: number,
    index: number,
): { x: number; y: number; angle: number } {
    const ANGLE_SHIFT_FOR_TOP_ALIGNMENT = numberOfPetals === 5 ? 18 : 0;
    const angle = (index * 360) / numberOfPetals - ANGLE_SHIFT_FOR_TOP_ALIGNMENT;
    const x = radius * Math.cos((angle * Math.PI) / 180);
    const y = radius * Math.sin((angle * Math.PI) / 180);
    return { x, y, angle };
}

export function formatPetalPosition(numberOfPetals: number, radius: number, index: number): string {
    const { x, y } = calculatePetalPosition(numberOfPetals, radius, index);
    return `translate(-50%, -50%) translate(${x}px, ${y}px)`;
}

const centerPointStyles = {
    aspectRatio: "1",
    borderRadius: "50%",
    background: senecPrimaryColors.white,
    width: { xsMobile: "30px", xs: "40px" },
    borderColor: senecSecondaryColors.neutralGray20,
    borderStyle: "solid",
    borderWidth: 0.5,
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 50,
};
